import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { signMessage } from '@/lib/web3';
import { useToast } from '@/hooks/use-toast';
import { setAuthToken, getAuthToken, clearAuthTokens, isTokenExpired } from '@/lib/auth-utils';
import { resetCircuitBreaker } from '@/lib/api-client';
import { UserRole } from '@shared/auth-simple';
import { isDevelopment } from '@/config/environment';
import { showAuthErrorToast, showErrorToast } from '@/lib/toast-errors';

// Storage keys for impersonation state
const IMPERSONATION_KEY = 'impersonation_state';
const ORIGINAL_TOKEN_KEY = 'original_admin_token';

export interface User {
  id: string;
  walletAddress?: string;
  email?: string;
  username?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  role: UserRole | string;
  authMethod: 'wallet' | 'email';
  emailVerified?: boolean;
  xp: number;
  level: number;
  avatarCredits: number;
  totalAvatarsGenerated: number;
  profileImage?: string;
  bio?: string;
  timezone?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ImpersonationState {
  isImpersonating: boolean;
  originalUser: User | null;
  impersonatedUser: User | null;
  impersonationStartTime: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (_walletAddress: string) => Promise<void>;
  loginWithCredentials: (_email: string, _password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => void;
  // Impersonation features
  isImpersonating: boolean;
  originalUser: User | null;
  impersonatedUser: User | null;
  impersonationStartTime: string | null;
  startImpersonation: (_userId: string) => Promise<boolean>;
  endImpersonation: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Impersonation state
  const [impersonationState, setImpersonationState] = useState<ImpersonationState>(() => {
    try {
      const saved = sessionStorage.getItem(IMPERSONATION_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {
      // Ignore parse errors
    }
    return {
      isImpersonating: false,
      originalUser: null,
      impersonatedUser: null,
      impersonationStartTime: null,
    };
  });

  // Persist impersonation state to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonationState));
    } catch {
      // Ignore storage errors
    }
  }, [impersonationState]);

  // Ref to track if auth has been resolved (avoids stale closure in timeout)
  const authResolvedRef = useRef(false);

  const isAuthenticated = !!user;

  // Check for existing auth on mount
  useEffect(() => {
    // CRITICAL FIX: Always fetch real user data from database - NO MOCK DATA
    // Development bypass removed per user requirement: no mock/hardcoded data ever

    // SECURITY: Add timeout to prevent infinite loading spinner
    // If auth check takes longer than 15 seconds, assume failure
    const AUTH_TIMEOUT_MS = 15000;
    const timeoutId = setTimeout(() => {
      if (!authResolvedRef.current) {
        console.warn('[Auth] Auth check timed out after 15s, clearing loading state');
        authResolvedRef.current = true;
        setIsLoading(false);
        setUser(null);
      }
    }, AUTH_TIMEOUT_MS);

    const token = getAuthToken();
    if (token) {
      // Validate token before using it - prevents unnecessary API calls with expired tokens
      if (isTokenExpired(token)) {
        console.warn('Token expired on mount. Clearing invalid tokens.');

        // Log for security monitoring
        import('@/lib/security-monitor')
          .then(({ logTokenExpiredOnMount }) => {
            logTokenExpiredOnMount();
          })
          .catch(() => {});

        clearAuthTokens();

        // Get fresh token in development
        if (isDevelopment) {
          getDevToken();
        } else {
          authResolvedRef.current = true;
          setIsLoading(false);
        }
        return () => clearTimeout(timeoutId);
      }

      // Token is valid, fetch current user
      fetchCurrentUser();
    } else if (isDevelopment) {
      getDevToken();
    } else {
      authResolvedRef.current = true;
      setIsLoading(false);
    }

    return () => clearTimeout(timeoutId);
  }, []);

  const getDevToken = async () => {
    try {
      const response = await apiRequest('GET', '/api/auth/dev-token');
      const { token, accessToken, user: userData } = await response.json();

      // SECURITY: Refresh token is set as httpOnly cookie by server
      // We only store the access token
      if (accessToken) {
        const result = setAuthToken(accessToken);
        if (!result.success) {
          console.error('Failed to store dev token:', result.error);
          authResolvedRef.current = true;
          setIsLoading(false);
          return;
        }
      } else if (token) {
        // Fallback for legacy single token
        localStorage.setItem('token', token);
      }

      setUser(userData);
      authResolvedRef.current = true;
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to get dev token:', error);
      authResolvedRef.current = true;
      setIsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const _token = getAuthToken();

      const response = await apiRequest('GET', '/api/auth/me');

      // Validate response before parsing
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Validate user data exists in response
      if (!data || !data.user) {
        console.warn('Invalid response from /api/auth/me - missing user data');
        clearAuthTokens();
        setUser(null);
        return;
      }

      setUser(data.user);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('fetchCurrentUser failed:', errorMessage);

      // Only log non-401 errors to reduce console noise
      if (!errorMessage.includes('401')) {
        console.error('Failed to fetch current user:', error);
      }

      // Clear invalid tokens using proper cleanup
      clearAuthTokens();

      setUser(null);
    } finally {
      authResolvedRef.current = true;
      setIsLoading(false);
    }
  };

  const login = useCallback(
    async (walletAddress: string) => {
      try {
        setIsLoading(true);

        const nonceResponse = await apiRequest(
          'GET',
          `/api/auth/nonce?walletAddress=${walletAddress}`,
        );
        const { nonce } = await nonceResponse.json();

        const signature = await signMessage(nonce, walletAddress);

        const verifyResponse = await apiRequest('POST', '/api/auth/verify', {
          walletAddress,
          signature,
          message: nonce,
        });

        const { token, accessToken, user: userData } = await verifyResponse.json();

        // SECURITY: Refresh token is set as httpOnly cookie by server
        // We only store the access token
        if (accessToken) {
          const result = setAuthToken(accessToken);
          if (!result.success) {
            throw new Error(`Failed to store token: ${result.error}`);
          }
        } else if (token) {
          // Fallback for legacy single token
          localStorage.setItem('token', token);
        }
        setUser(userData);

        // Reset circuit breaker after successful login to allow token refresh
        resetCircuitBreaker();

        queryClient.invalidateQueries();

        toast({
          title: 'Welcome to White Rabbit Academy!',
          description: `Successfully authenticated as ${userData.username || 'Anonymous Seeker'}`,
        });
      } catch (error: unknown) {
        console.error('Login failed:', error);

        // Handle wallet-specific errors with custom messages
        const errorMessage = error instanceof Error ? error.message : '';
        if (errorMessage.includes('User rejected')) {
          showErrorToast(error, {
            title: 'Signature Rejected',
            showCode: false,
          });
        } else if (errorMessage.includes('Invalid signature')) {
          showErrorToast(error, {
            title: 'Invalid Signature',
            showCode: false,
          });
        } else {
          showAuthErrorToast(error);
        }

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient, toast],
  );

  const loginWithCredentials = useCallback(
    async (emailOrUsername: string, password: string) => {
      try {
        setIsLoading(true);

        const response = await apiRequest('POST', '/api/auth/login', {
          emailOrUsername,
          password,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Login failed');
        }

        const { token, accessToken, user: userData } = await response.json();

        // SECURITY: Refresh token is set as httpOnly cookie by server
        // We only store the access token
        if (accessToken) {
          const result = setAuthToken(accessToken);
          if (!result.success) {
            throw new Error(`Failed to store token: ${result.error}`);
          }
        } else if (token) {
          // Fallback for legacy single token
          localStorage.setItem('token', token);
        }
        setUser(userData);

        // Reset circuit breaker after successful login to allow token refresh
        resetCircuitBreaker();

        queryClient.invalidateQueries();

        toast({
          title: 'Welcome Back!',
          description: `Successfully signed in as ${userData.firstName || userData.username || 'Academy Member'}`,
        });
      } catch (error: unknown) {
        console.error('Login failed:', error);
        showAuthErrorToast(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient, toast],
  );

  const refreshUser = useCallback(async () => {
    await fetchCurrentUser();
  }, []);

  const logout = useCallback(async () => {
    // Clear impersonation state if active
    if (impersonationState.isImpersonating) {
      sessionStorage.removeItem(ORIGINAL_TOKEN_KEY);
      sessionStorage.removeItem(IMPERSONATION_KEY);
      setImpersonationState({
        isImpersonating: false,
        originalUser: null,
        impersonatedUser: null,
        impersonationStartTime: null,
      });
    }

    // Clear tokens and user state
    clearAuthTokens();
    setUser(null);
    queryClient.clear();

    const authMethod = user?.authMethod;
    const logoutMessage =
      authMethod === 'wallet'
        ? 'Your wallet has been disconnected successfully.'
        : 'You have been signed out successfully.';

    toast({
      title: 'Signed Out',
      description: logoutMessage,
    });
  }, [user?.authMethod, queryClient, toast, impersonationState.isImpersonating]);

  // Start impersonating a user
  const startImpersonation = useCallback(
    async (userId: string): Promise<boolean> => {
      try {
        // Store current admin token before impersonation
        const currentToken = getAuthToken();
        if (!currentToken || !user) {
          toast({
            title: 'Error',
            description: 'You must be logged in to impersonate users',
            variant: 'destructive',
          });
          return false;
        }

        // Call backend to start impersonation
        const response = await apiRequest('POST', `/api/admin/impersonate/${userId}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to start impersonation');
        }

        const data = await response.json();
        const { token, user: impersonatedUserData } = data;

        // Store original admin token in sessionStorage for session restoration
        // SECURITY NOTE: sessionStorage is vulnerable to XSS. This is an accepted
        // trade-off for admin-only impersonation feature. Mitigations:
        // 1. Strict CSP headers prevent most XSS
        // 2. Short impersonation sessions (1 hour max)
        // 3. Admin-only feature with audit logging
        // 4. Token not returned from server (client already has it)
        sessionStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken);

        // Set the new impersonation token
        const tokenResult = setAuthToken(token);
        if (!tokenResult.success) {
          throw new Error('Failed to set impersonation token');
        }

        // Update impersonation state
        setImpersonationState({
          isImpersonating: true,
          originalUser: user,
          impersonatedUser: impersonatedUserData,
          impersonationStartTime: new Date().toISOString(),
        });

        // Update the current user
        setUser(impersonatedUserData);

        // Clear query cache to refresh data as impersonated user
        queryClient.clear();

        toast({
          title: 'Impersonation Started',
          description: `You are now viewing as ${impersonatedUserData.firstName || impersonatedUserData.username || 'User'}`,
        });

        return true;
      } catch (error) {
        console.error('Failed to start impersonation:', error);
        toast({
          title: 'Impersonation Failed',
          description: error instanceof Error ? error.message : 'An error occurred',
          variant: 'destructive',
        });
        return false;
      }
    },
    [user, queryClient, toast],
  );

  // End impersonation and restore admin session
  const endImpersonation = useCallback(async (): Promise<boolean> => {
    try {
      // Call backend to end impersonation
      const response = await apiRequest('POST', '/api/admin/impersonate/end');

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to end impersonation');
      }

      const data = await response.json();
      const { user: adminUserData } = data;

      // Restore original admin token
      const originalToken = sessionStorage.getItem(ORIGINAL_TOKEN_KEY);
      if (originalToken) {
        const tokenResult = setAuthToken(originalToken);
        if (!tokenResult.success) {
          // Fallback: clear all auth and let user re-login
          clearAuthTokens();
          throw new Error('Failed to restore admin session. Please log in again.');
        }
        sessionStorage.removeItem(ORIGINAL_TOKEN_KEY);
      }

      // Clear impersonation state
      const savedOriginalUser = impersonationState.originalUser;
      setImpersonationState({
        isImpersonating: false,
        originalUser: null,
        impersonatedUser: null,
        impersonationStartTime: null,
      });

      // Restore admin user — set synchronously BEFORE invalidating queries
      // to prevent ProtectedRoute from seeing a brief unauthenticated state
      setUser(adminUserData || savedOriginalUser);

      // Invalidate (not clear) query cache — refetches in background
      // without destroying cached data, preventing auth flicker
      queryClient.invalidateQueries();

      toast({
        title: 'Impersonation Ended',
        description: 'You have returned to your admin account',
      });

      return true;
    } catch (error) {
      console.error('Failed to end impersonation:', error);

      // Emergency fallback: clear everything and force re-login
      clearAuthTokens();
      sessionStorage.removeItem(ORIGINAL_TOKEN_KEY);
      sessionStorage.removeItem(IMPERSONATION_KEY);

      setImpersonationState({
        isImpersonating: false,
        originalUser: null,
        impersonatedUser: null,
        impersonationStartTime: null,
      });

      setUser(null);
      queryClient.clear();

      toast({
        title: 'Session Ended',
        description: 'Please log in again',
        variant: 'destructive',
      });

      return false;
    }
  }, [queryClient, toast, impersonationState.originalUser]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      loginWithCredentials,
      refreshUser,
      logout,
      // Impersonation features
      isImpersonating: impersonationState.isImpersonating,
      originalUser: impersonationState.originalUser,
      impersonatedUser: impersonationState.impersonatedUser,
      impersonationStartTime: impersonationState.impersonationStartTime,
      startImpersonation,
      endImpersonation,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      login,
      loginWithCredentials,
      refreshUser,
      logout,
      impersonationState,
      startImpersonation,
      endImpersonation,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
